import { useState, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, List, AlertCircle, Plus, Minus, Info } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Pro017List } from "../create-pro017-form";

export interface Pro017StepThreeData {
  lists: Pro017List[];
}

interface StepThreeProps {
  data: Pro017StepThreeData;
  updateData: (data: Partial<Pro017StepThreeData>) => void;
  nextStep: () => void;
  prevStep: () => void;
}

export default function StepThree({ data, updateData, nextStep, prevStep }: StepThreeProps) {
  const [errors, setErrors] = useState<Record<string, string>>({});

  const isFormValid = useMemo(() => {
    return data.lists.length >= 2 && data.lists.length <= 10;
  }, [data.lists.length]);

  const validateStep = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Mínimo 2 listas
    if (data.lists.length < 2) {
      newErrors.lists = "Campanha deve ter no mínimo 2 listas";
    }

    // Máximo 10 listas
    if (data.lists.length > 10) {
      newErrors.lists = "Campanha deve ter no máximo 10 listas";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleAddList = () => {
    if (data.lists.length >= 10) {
      setErrors({ ...errors, lists: "Máximo de 10 listas atingido" });
      return;
    }

    const newListNumber = data.lists.length + 1;
    updateData({
      lists: [...data.lists, { listNumber: newListNumber, items: [] }]
    });
    setErrors({});
  };

  const handleRemoveList = () => {
    if (data.lists.length <= 2) {
      setErrors({ ...errors, lists: "Mínimo de 2 listas necessário" });
      return;
    }

    updateData({
      lists: data.lists.slice(0, -1)
    });
    setErrors({});
  };

  const handleNext = () => {
    if (validateStep()) {
      nextStep();
    }
  };

  return (
    <div className="space-y-4 animate-in fade-in duration-300">
      <div className="bg-card rounded-lg border p-4 space-y-4">
        <div className="flex items-center gap-2">
          <List className="h-5 w-5 text-primary" />
          <div>
            <h3 className="text-base font-semibold">
              Quantidade de Listas *
            </h3>
            <p className="text-sm text-muted-foreground">
              Defina quantas listas de produtos terá o combo (mínimo 2, máximo 10)
            </p>
          </div>
        </div>

        {/* List Counter */}
        <div className="flex items-center justify-between p-4 bg-primary/5 rounded-lg border border-primary/20">
          <div>
            <p className="text-sm font-medium">
              Número de Listas
            </p>
            <p className="text-xs text-muted-foreground">
              Cliente precisará comprar 1 item de cada lista
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleRemoveList}
              disabled={data.lists.length <= 2}
              className="h-9 w-9 p-0"
            >
              <Minus className="h-4 w-4" />
            </Button>
            <Badge
              variant="default"
              className="text-lg px-4 py-2 font-bold"
            >
              {data.lists.length}
            </Badge>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleAddList}
              disabled={data.lists.length >= 10}
              className="h-9 w-9 p-0"
            >
              <Plus className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Lists Preview */}
        <div className="space-y-2">
          <p className="text-sm font-medium">
            Prévia das Listas
          </p>
          <div className="grid gap-2">
            {data.lists.map((list) => (
              <div
                key={list.listNumber}
                className="flex items-center gap-3 p-3 rounded-lg border bg-card hover:border-primary/50 transition-colors"
              >
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                  <span className="font-bold text-primary">{list.listNumber}</span>
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium">
                    Lista {list.listNumber}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {list.items.length === 0
                      ? "Nenhum produto adicionado ainda"
                      : `${list.items.length} produto(s) adicionado(s)`}
                  </p>
                </div>
                <Badge variant={list.items.length >= 2 ? "default" : "outline"}>
                  {list.items.length >= 2 ? "Completa" : "Pendente"}
                </Badge>
              </div>
            ))}
          </div>
        </div>

        {errors.lists && (
          <p className="text-sm text-destructive flex items-center gap-1">
            <AlertCircle className="w-3 h-3" />
            {errors.lists}
          </p>
        )}

        {/* Info Box */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
          <div className="flex items-start gap-2">
            <Info className="h-4 w-4 text-blue-600 mt-0.5" />
            <div className="text-xs text-blue-900">
              <p className="font-semibold mb-1">Como funciona:</p>
              <ul className="space-y-1">
                <li>• Cada lista deve ter no mínimo 2 produtos</li>
                <li>• Cliente escolhe 1 produto de cada lista</li>
                <li>• O desconto é aplicado ao comprar 1 item de cada lista</li>
                <li>• Exemplo com 3 listas: 1 mochila + 1 estojo + 1 caderno = combo completo</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <div className="flex justify-between pt-2">
        <Button
          variant="outline"
          onClick={prevStep}
          className="w-full md:w-auto"
        >
          <ChevronLeft className="mr-2 h-4 w-4" />
          Voltar
        </Button>
        <Button
          onClick={handleNext}
          disabled={!isFormValid}
          className="w-full md:w-auto"
        >
          Continuar
          <ChevronRight className="ml-2 h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
